import React, { useState, useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import {
  DatePicker,
  Icon,
  SegmentedButtons,
  SelectMenu,
  TextField,
  Typography,
} from '@scale/scaleui';
import { TimePicker } from './TimePicker';
import uuid from 'uuid';
import { RepeatType } from '../types';
import { RecurringType } from '@scale/shared/webinarSessions/types';

import {
  calculateFutureOccurrences,
  timeDifferenceInMinutes,
  addDurationToTimeString,
  getNewStartDateTime,
  getStartTimeFromDate,
  getEndTimeFromStartTime,
} from '../hooks/calculateFutureOccurrences';

interface SchedulerProps {
  recurring: RecurringType;
  setRecurring: (r: RecurringType) => void;
  eventStart: Date | null;
  setEventStart: (d: Date | null) => void;
  duration: number | null;
  setDuration: (name: number | null) => void;
  occurrences: Date[] | null;
  setOccurrences: (o: Date[] | null) => void;
}

const DAYS_OF_THE_WEEK = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

// TODO: default state from props for editing existing sessions
export const Scheduler: React.FC<SchedulerProps> = ({
  recurring,
  setRecurring,
  eventStart,
  setEventStart,
  setDuration,
  occurrences,
  setOccurrences,
}) => {
  const [numOccurrences, setNumOccurrences] = useState<number>(1);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const setDate = (date: Date) => {
    setSelectedDate(new Date(date.toDateString()));
  };
  const [startTime, setStartTime] = useState<string>(getStartTimeFromDate(new Date()));
  const [endTime, setEndTime] = useState<string>(getEndTimeFromStartTime(startTime));
  const startEndTimeError = useMemo(
    () => timeDifferenceInMinutes(endTime, startTime) < 0,
    [startTime, endTime],
  );
  const [repeatNumber, setRepeatNumber] = useState<number | null>(null);
  const [repeatType, setRepeatType] = useState<RepeatType>(RepeatType.DAYS);
  const [daysOfTheWeek, setDaysOfTheWeek] = useState<string[]>([]);
  const [additionalTimesOfDay, setAdditionalTimesOfDay] = useState<string[]>([]);
  const handleDaysOfTheWeekClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, day: string) => {
      if (daysOfTheWeek.includes(day)) setDaysOfTheWeek(daysOfTheWeek.filter(d => d !== day));
      else setDaysOfTheWeek([...daysOfTheWeek, day]);
      e.currentTarget.blur();
    },
    [daysOfTheWeek, setDaysOfTheWeek],
  );
  const handleAddTimeOfDay = useCallback(() => {
    setNumOccurrences(nO => {
      if (nO <= additionalTimesOfDay.length + 1) return additionalTimesOfDay.length + 2;
      else return nO;
    });
    setAdditionalTimesOfDay(a => [...a, '08:00AM']);
  }, [additionalTimesOfDay.length]);
  const handleRemoveTimeOfDay = useCallback(
    () => setAdditionalTimesOfDay(a => [...a.slice(0, a.length - 1)]),
    [setAdditionalTimesOfDay],
  );
  const handleSetAdditionalTimeOfDay = useCallback(
    (time: string, i: number) => {
      setAdditionalTimesOfDay(a => [...a.slice(0, i), time, ...a.slice(i + 1)]);
    },
    [setAdditionalTimesOfDay],
  );

  const duration = useMemo(() => {
    const d = timeDifferenceInMinutes(endTime, startTime);
    setDuration(d);
    return d;
  }, [setDuration, startTime, endTime]);

  const futureOccurrences = useMemo(
    () =>
      calculateFutureOccurrences({
        eventStart,
        recurring,
        numOccurrences,
        repeatNumber,
        repeatType,
        daysOfTheWeek,
        selectedDate,
        additionalTimesOfDay,
      }),
    [
      eventStart,
      recurring,
      numOccurrences,
      repeatNumber,
      repeatType,
      daysOfTheWeek,
      selectedDate,
      additionalTimesOfDay,
    ],
  );

  useEffect(
    () => setEventStart(getNewStartDateTime(selectedDate, startTime)),
    [setEventStart, selectedDate, startTime],
  );
  useEffect(() => setOccurrences(futureOccurrences), [futureOccurrences, setOccurrences]);

  return (
    <div>
      <Typography.Subtitle3 className="text-neutral-600 mb-1">
        Scheduled Date & Time (Local Timezone - {Intl.DateTimeFormat().resolvedOptions().timeZone})
      </Typography.Subtitle3>
      <div className="ml-4">
        <SegmentedButtons
          selected={recurring}
          onSelect={(id: string) => setRecurring(id as RecurringType)}
          buttons={Object.values(RecurringType)
            .filter(t => t !== RecurringType.MULTI)
            .map(t => ({ id: t, label: t }))}
          className="mb-1"
        />
        <div className="flex gap-2 mb-1">
          <div className="flex flex-col gap-1">
            <Typography.Subtitle3 className="text-neutral-600">Date</Typography.Subtitle3>
            <DatePicker
              value={selectedDate}
              onChange={setDate}
              placeholder={new Date().toString()}
              minDate={new Date()}
            />
          </div>
          <div className="flex flex-col gap-1">
            <TimePicker label="Start Time" time={startTime} setTime={setStartTime} />
            {recurring === RecurringType.RECURRING &&
              additionalTimesOfDay.map((time: string, i: number) => (
                <TimePicker
                  key={`${i}-${time}`}
                  label="Start Time"
                  time={time}
                  setTime={(t: string) => handleSetAdditionalTimeOfDay(t, i)}
                  error={[
                    startTime,
                    ...additionalTimesOfDay.slice(0, i),
                    ...additionalTimesOfDay.slice(i + 1),
                  ].includes(time)}
                />
              ))}
          </div>
          <div className="flex flex-col gap-1">
            <TimePicker
              label="End Time"
              time={endTime}
              setTime={setEndTime}
              error={startEndTimeError}
            />
            {recurring === RecurringType.RECURRING &&
              additionalTimesOfDay.map((time: string, i: number) => (
                <div key={`${i}-${time}-end`} className="flex flex-col gap-3">
                  <Typography.Subtitle3>End Time</Typography.Subtitle3>
                  <div className="flex gap-4 mb-[10px] mt-[2px] items-start">
                    {addDurationToTimeString(time, duration)}
                    {i === additionalTimesOfDay.length - 1 && (
                      <Icon
                        size="sm"
                        icon={['far', 'trash']}
                        variant="neutral"
                        rounded
                        onClick={handleRemoveTimeOfDay}
                        className="mt-[-4px]"
                      />
                    )}
                  </div>
                </div>
              ))}
          </div>
          {recurring === RecurringType.RECURRING && (
            <Icon
              className="mt-auto mb-[6px]"
              icon="plus"
              variant="neutral"
              rounded
              onClick={handleAddTimeOfDay}
              tooltip="Add another session on the same day"
              tooltipProps={{ side: 'left' }}
            />
          )}
        </div>
        {recurring === RecurringType.RECURRING && (
          <div className="flex gap-2 mb-1">
            <TextField
              label="Repeat every"
              type="number"
              value={repeatNumber ?? ''}
              onChange={e => setRepeatNumber(Number(e.target.value))}
              max={7}
              min={1}
              step={1}
            />
            <SelectMenu
              label=":"
              options={Object.values(RepeatType).map(t => ({ id: t, name: t }))}
              selectedOptionId={repeatType}
              setSelected={setRepeatType}
            />
            {repeatType === RepeatType.WEEKS && (
              <div className="flex gap-1 mt-8">
                {DAYS_OF_THE_WEEK.map((day: string) => (
                  <Icon
                    key={day}
                    variant="primary"
                    size="sm"
                    rounded
                    onClick={e =>
                      handleDaysOfTheWeekClick(e as React.MouseEvent<HTMLElement, MouseEvent>, day)
                    }
                    className={twMerge(
                      'bg-primary-100 hover:bg-primary-200 selected:bg-primary-300 focus:bg-primary-300',
                      daysOfTheWeek.includes(day) &&
                        'bg-primary-300 hover:bg-primary-200 selected:bg-primary-100 focus:bg-primary-100',
                    )}
                  >
                    {day[0].toUpperCase()}
                  </Icon>
                ))}
              </div>
            )}
          </div>
        )}
        {recurring === RecurringType.RECURRING && (
          <div className="w-6/12">
            <TextField
              label="Number of occurences (max 20)"
              type="number"
              value={numOccurrences}
              onChange={e => setNumOccurrences(Number(e.target.value))}
              max={20}
              min={1 + additionalTimesOfDay.length}
              step={1}
            />
          </div>
        )}
        {!!occurrences?.length && (
          <div className="flex flex-col mt-1">
            <Typography.Subtitle3 className="mb-1">Scheduled Recurrences</Typography.Subtitle3>
            <div className="flex flex-col max-h-[80px] ml-4 flex-wrap">
              {_.uniq(occurrences)
                .sort((a: Date, b: Date) => a.getTime() - b.getTime())
                .map((o: Date) => (
                  <Typography.Paragraph3
                    key={`${o.toLocaleString()}_${uuid.v4().substring(0, 6)}`}
                    className="ml-2"
                  >
                    • {o.toLocaleString()}
                  </Typography.Paragraph3>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
